/** Aggregates various task mutation hooks into a single unified interface. */
// @ts-check
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import useTaskStructure from '@/features/tasks/hooks/mutations/use-task-structure.js';
import useTaskData from '@/features/tasks/api/use-task-data';
import useBulkTaskUpdate from '@/features/tasks/api/use-bulk-task-update.js';
import useToggleTaskComplete from '@/features/tasks/hooks/mutations/use-task-toggle.js';
import {
  useFollowUpToast,
  toTaskMutationsContextValue,
} from '@/features/tasks/hooks/mutations/use-task-actions.helpers';
import { useTaskUpdateCallbacks } from '@/features/tasks/hooks/mutations/use-task-actions-update';
import { useTaskDeleteAndFocusCallbacks } from '@/features/tasks/hooks/mutations/use-task-actions-focus';

/**
 * Main aggregator hook for all task-related mutations.
 * Provides a unified interface for creating, updating, deleting, and completing tasks.
 *
 * @param {Object} [options]
 * @param {(taskId: string) => void} [options.startFocus] Optional callback for when a task enters focus mode.
 * @returns {TaskMutationsContextValue} An object containing all available task mutation functions.
 */
const useTaskMutations = ({ startFocus } = {}) => {
  const { allTasks, taskTree, getAllDescendantIds } = useTasksContext();
  const { updateTaskDetails, updateTaskDuration, handleDeleteTask } =
    useTaskData();
  const { moveTask, rebalanceOrderKeys } = useTaskStructure();
  const { updateMultipleTasks } = useBulkTaskUpdate();
  const { handleToggleComplete: baseToggleComplete } = useToggleTaskComplete();
  const { handleFollowUpRequest, showFollowUpToast } = useFollowUpToast();

  const { updateTask, handleToggleComplete } = useTaskUpdateCallbacks({
    allTasks,
    taskTree,
    getAllDescendantIds,
    updateTaskDetails,
    moveTask,
    baseToggleComplete,
    showFollowUpToast,
  });

  const { deleteTask, handleStartFocus } = useTaskDeleteAndFocusCallbacks({
    taskTree,
    getAllDescendantIds,
    handleDeleteTask,
    startFocus,
    updateTask,
  });

  return toTaskMutationsContextValue({
    updateTask,
    deleteTask,
    handleToggleComplete,
    handleFollowUpRequest,
    moveTask,
    rebalanceOrderKeys,
    updateTaskDuration,
    updateMultipleTasks,
    handleStartFocus,
  });
};

export default useTaskMutations;
